import axios from "axios";
import { Auth0Client } from "@auth0/auth0-spa-js";
import { apiConfig } from "../../config";
import {
  AcceptTermsPayload,
  TermsAcceptance,
  TermsAcceptanceStatus,
  TermsVersion,
} from "../../types/terms";

class TermsClient {
  private auth0Client: Auth0Client;

  constructor(auth0Client: Auth0Client) {
    this.auth0Client = auth0Client;
  }

  private async getAuthHeaders() {
    const token = await this.auth0Client.getTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async getCurrent(typeKey: string): Promise<TermsVersion | null> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/terms-and-conditions/types/${typeKey}/current`,
      { headers },
    );
    return response.data;
  }

  async getAcceptanceStatus(
    organizationId: string,
    termsTypeKey = "subscription",
  ): Promise<TermsAcceptanceStatus> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/terms-and-conditions/acceptances/status`,
      {
        headers,
        params: { organizationId, termsTypeKey },
      },
    );
    return response.data;
  }

  async listAcceptances(
    organizationId: string,
    options?: { termsTypeKey?: string; includeSignature?: boolean },
  ): Promise<TermsAcceptance[]> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/terms-and-conditions/acceptances`,
      {
        headers,
        params: {
          organizationId,
          termsTypeKey: options?.termsTypeKey,
          includeSignature:
            options?.includeSignature === true ? "true" : undefined,
        },
      },
    );
    return response.data;
  }

  async getVersion(versionId: string): Promise<TermsVersion> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/terms-and-conditions/versions/${versionId}`,
      { headers },
    );
    return response.data;
  }

  async accept(
    versionId: string,
    payload: AcceptTermsPayload,
  ): Promise<unknown> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/terms-and-conditions/versions/${versionId}/accept`,
      payload,
      { headers },
    );
    return response.data;
  }
}

export default TermsClient;
